import { useCallback, useEffect, useRef, useState } from "react";
import { APP_LOCALE } from "../config/locale";
import { Modal } from "./Modal";
import { ListToolbar } from "./ListToolbar";
import {
  ApiError,
  deleteEntityDocument,
  getEntityDocumentFileUrl,
  getEntityDocuments,
  updateEntityDocument,
  uploadEntityDocument,
  type DocumentOwnerType,
  type DocumentSection,
  type EntityDocumentDto,
  type UpdateEntityDocumentPayload,
} from "../documents/documentsApi";

export interface EntityDocumentsSectionProps {
  ownerType: DocumentOwnerType;
  ownerId: number;
  section: DocumentSection;
  title?: string;
  // Suggested category options for the add/edit form (a free "Outro…" entry is always allowed via datalist).
  categories?: string[];
  // Show the "Contraparte" column + field (contracts).
  showCounterparty?: boolean;
  // card = full section with bg-card wrapper; inline = compact (e.g. comprovativo sub-panel).
  variant?: "card" | "inline";
  // Hide the section heading (e.g. in the obra tab where the active pill already names it).
  hideTitle?: boolean;
  emptyLabel?: string;
}

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const formatSize = (bytes?: number | null): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileNameWithoutExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
};

// Editable metadata shared by add form + edit row.
interface DocForm {
  title: string;
  category: string;
  counterpartyName: string;
  reference: string;
  documentDate: string;
  notes: string;
}

const emptyForm = (): DocForm => ({
  title: "",
  category: "",
  counterpartyName: "",
  reference: "",
  documentDate: "",
  notes: "",
});

const formFromDocument = (doc: EntityDocumentDto): DocForm => ({
  title: doc.title ?? "",
  category: doc.category ?? "",
  counterpartyName: doc.counterpartyName ?? "",
  reference: doc.reference ?? "",
  documentDate: toInputDate(doc.documentDate),
  notes: doc.notes ?? "",
});

const toPayload = (form: DocForm): UpdateEntityDocumentPayload => ({
  title: form.title.trim(),
  category: form.category.trim() || null,
  counterpartyName: form.counterpartyName.trim() || null,
  reference: form.reference.trim() || null,
  documentDate: form.documentDate || null,
  notes: form.notes.trim() || null,
});

export function EntityDocumentsSection({
  ownerType,
  ownerId,
  section,
  title,
  categories,
  showCounterparty = false,
  variant = "card",
  hideTitle = false,
  emptyLabel,
}: EntityDocumentsSectionProps) {
  const [documents, setDocuments] = useState<EntityDocumentDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [filter, setFilter] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  // [2.1.12] Multi-upload: o ref guarda N ficheiros; 1 ficheiro = lote de 1 (caminho single-file mantém-se).
  const pendingFilesRef = useRef<File[]>([]);
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [batchSummary, setBatchSummary] = useState<string | null>(null);
  const [form, setForm] = useState<DocForm>(emptyForm());

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DocForm | null>(null);

  const isInline = variant === "inline";
  const datalistId = `doc-categories-${ownerType}-${ownerId}-${section}`;

  const loadDocuments = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const result = await getEntityDocuments(ownerType, ownerId, {
          section,
        });
        setDocuments(result);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Não foi possível carregar os documentos.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [ownerType, ownerId, section],
  );

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const updateForm = (patch: Partial<DocForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const updateDraft = (patch: Partial<DocForm>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleFileSelected = (files: FileList | null) => {
    const list = files ? Array.from(files) : [];
    pendingFilesRef.current = list;
    setPendingFileName(
      list.length === 0
        ? null
        : list.length === 1
          ? list[0].name
          : `${list.length} ficheiros selecionados`,
    );
    // Default the title to the file name (without extension) when a single file is picked
    // and the title is still empty. For multi-select each file uses its own name as title.
    if (list.length === 1 && form.title.trim() === "") {
      updateForm({ title: fileNameWithoutExtension(list[0].name) });
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    pendingFilesRef.current = [];
    setPendingFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setIsCreateOpen(false);
  };

  const handleUpload = async () => {
    if (isUploading) return;
    const files = pendingFilesRef.current;
    if (files.length === 0) {
      setError("Selecione um ficheiro para carregar.");
      return;
    }
    // Single file: respect a title the user typed; otherwise default to the file name.
    // Multi file: each document is titled by its own file name (the shared title field is ignored).
    const isSingle = files.length === 1;
    if (isSingle) {
      const resolvedTitle = form.title.trim() || fileNameWithoutExtension(files[0].name);
      if (!resolvedTitle) {
        setError("O título é obrigatório.");
        return;
      }
    }
    // Metadata shared across every file in the batch.
    const sharedMeta = {
      category: form.category.trim() || null,
      counterpartyName: showCounterparty ? form.counterpartyName.trim() || null : null,
      reference: form.reference.trim() || null,
      documentDate: form.documentDate || null,
      notes: form.notes.trim() || null,
    };
    setIsUploading(true);
    setError(null);
    setBatchSummary(null);
    try {
      const results = await Promise.allSettled(
        files.map((file) =>
          uploadEntityDocument({
            file,
            ownerType,
            ownerId,
            section,
            title: isSingle
              ? form.title.trim() || fileNameWithoutExtension(file.name)
              : fileNameWithoutExtension(file.name),
            ...sharedMeta,
          }),
        ),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) {
        resetForm();
        // Re-fetch so the server stays the source of truth.
        await loadDocuments(true);
      }
      if (failed > 0) {
        setBatchSummary(
          ok > 0
            ? `${ok} de ${results.length} enviado(s); ${failed} falhou/falharam.`
            : `Falha ao carregar ${failed} documento(s).`,
        );
      } else if (results.length > 1) {
        setBatchSummary(`${ok} documento(s) enviado(s).`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar o documento.");
    } finally {
      setIsUploading(false);
    }
  };

  const openEdit = (doc: EntityDocumentDto) => {
    setEditingId(doc.id);
    setDraft(formFromDocument(doc));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const saveEdit = async () => {
    if (editingId === null || !draft) return;
    if (!draft.title.trim()) {
      setError("O título é obrigatório.");
      return;
    }
    setError(null);
    try {
      await updateEntityDocument(editingId, toPayload(draft));
      setEditingId(null);
      setDraft(null);
      await loadDocuments(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível atualizar o documento.");
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar este documento?")) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteEntityDocument(id);
      setDocuments((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setDocuments((prev) => prev.filter((item) => item.id !== id));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar o documento.");
        loadDocuments(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const inputClass =
    "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";
  const linkAction =
    "underline cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed";

  const categoryField = (
    value: string,
    onChange: (next: string) => void,
    key: string,
  ) => (
    <label className="flex flex-col gap-1 text-sm text-secondary-text">
      Categoria
      <input
        list={categories && categories.length > 0 ? datalistId : undefined}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Categoria"
        className={inputClass}
        key={key}
      />
    </label>
  );

  const colSpan = showCounterparty ? 7 : 6;

  // Client-side filter: case-insensitive substring across the listed text columns.
  const normalizedFilter = filter.trim().toLowerCase();
  const shown =
    normalizedFilter === ""
      ? documents
      : documents.filter((doc) =>
          [doc.title, doc.category, doc.counterpartyName, doc.reference]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedFilter)),
        );

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={colSpan} className="px-4 py-6 text-center text-secondary-text">
            A carregar…
          </td>
        </tr>
      );
    }
    if (shown.length === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="px-4 py-6 text-center text-secondary-text">
            {filter !== "" ? "Sem resultados para o filtro." : emptyLabel ?? "Sem documentos."}
          </td>
        </tr>
      );
    }
    return shown.map((doc) => (
      <tr key={doc.id} className="border-b border-border last:border-b-0">
        <td className="px-3 py-2 break-words">
          {doc.title}
          {doc.notes && (
            <span className="block text-xs text-secondary-text mt-1 break-words">{doc.notes}</span>
          )}
        </td>
        <td className="px-3 py-2 break-words">{doc.category || "—"}</td>
        {showCounterparty && (
          <td className="px-3 py-2 break-words">{doc.counterpartyName || "—"}</td>
        )}
        <td className="px-3 py-2 break-words">{doc.reference || "—"}</td>
        <td className="px-3 py-2 whitespace-nowrap">{formatDate(doc.documentDate)}</td>
        <td className="px-3 py-2">
          <a
            className={`${linkAction} text-primary`}
            href={getEntityDocumentFileUrl(doc.id)}
            target="_blank"
            rel="noreferrer"
            title={doc.fileName}
          >
            Abrir
          </a>
          <span className="block text-xs text-secondary-text mt-1 whitespace-nowrap">
            {formatSize(doc.sizeInBytes)}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="flex gap-3 flex-wrap">
            <button
              className={`${linkAction} text-primary`}
              type="button"
              onClick={() => openEdit(doc)}
            >
              Editar
            </button>
            <button
              className={`${linkAction} text-destructive`}
              type="button"
              disabled={deletingId === doc.id}
              onClick={() => handleDelete(doc.id)}
            >
              {deletingId === doc.id ? "A eliminar…" : "Eliminar"}
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  const heading = title ?? "Documentos";

  const body = (
    <div className={isInline ? "space-y-3" : "space-y-4"}>
      {!hideTitle && (
        <h3
          className="text-foreground"
          style={{
            fontSize: isInline ? "var(--text-base)" : "var(--text-section-title)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          {heading}
        </h3>
      )}

      {/* Toolbar: instant filter + compact summary (left) + create CTA (right) — one clean row */}
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar documentos…"
        summary={<span className="text-secondary-text text-sm">{shown.length} documento(s)</span>}
      >
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Novo documento
        </button>
      </ListToolbar>

      {error && !isCreateOpen && editingId === null && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {batchSummary && (
        <div
          className="border border-border bg-muted text-foreground px-3 py-2 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {batchSummary}
        </div>
      )}

      {isCreateOpen && (
        <Modal
          title="Novo documento"
          onClose={resetForm}
          footer={
            <>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 min-h-[44px] bg-secondary hover:bg-accent border border-border rounded transition-colors"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={isUploading || !pendingFileName}
                className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isUploading ? "A carregar…" : "Carregar ficheiro"}
              </button>
            </>
          }
        >
          {error && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}
          {categories && categories.length > 0 && (
            <datalist id={datalistId}>
              {categories.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Título *
              <input
                type="text"
                value={form.title}
                onChange={(event) => updateForm({ title: event.target.value })}
                placeholder="Título do documento"
                className={inputClass}
              />
            </label>
            {categoryField(form.category, (next) => updateForm({ category: next }), "add-cat")}
            {showCounterparty && (
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Contraparte
                <input
                  type="text"
                  value={form.counterpartyName}
                  onChange={(event) => updateForm({ counterpartyName: event.target.value })}
                  placeholder="Cliente / fornecedor / subempreiteiro"
                  className={inputClass}
                />
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Referência
              <input
                type="text"
                value={form.reference}
                onChange={(event) => updateForm({ reference: event.target.value })}
                placeholder="Nº / referência"
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data
              <input
                type="date"
                value={form.documentDate}
                onChange={(event) => updateForm({ documentDate: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Notas
              <input
                type="text"
                value={form.notes}
                onChange={(event) => updateForm({ notes: event.target.value })}
                placeholder="Notas (opcional)"
                className={inputClass}
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors text-sm cursor-pointer"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Escolher ficheiro
            </button>
            <span className="text-sm text-secondary-text break-words">
              {pendingFileName ?? "Nenhum ficheiro selecionado"}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.dwg,.dxf"
              onChange={(event) => handleFileSelected(event.target.files)}
              style={{ display: "none" }}
            />
          </div>
          <p className="text-xs text-secondary-text">
            Pode escolher vários ficheiros — cada um fica com o seu nome como título e partilha os
            restantes campos (categoria, referência, data, notas).
          </p>
        </Modal>
      )}

      {/* List */}
      <div
        className="border border-border bg-card overflow-x-auto"
        style={{ borderRadius: "var(--radius)" }}
      >
        <table className="w-full" style={{ minWidth: showCounterparty ? 760 : 640 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-3 py-2">Título</th>
              <th className="px-3 py-2">Categoria</th>
              {showCounterparty && <th className="px-3 py-2">Contraparte</th>}
              <th className="px-3 py-2">Referência</th>
              <th className="px-3 py-2" style={{ width: 110 }}>Data</th>
              <th className="px-3 py-2" style={{ width: 100 }}>Ficheiro</th>
              <th className="px-3 py-2" style={{ width: 150 }}>Ações</th>
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editingId !== null && draft && (
        <Modal
          title="Editar documento"
          onClose={cancelEdit}
          footer={
            <>
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveEdit}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Guardar
              </button>
            </>
          }
        >
          {error && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {error}
            </div>
          )}
          {categories && categories.length > 0 && (
            <datalist id={`${datalistId}-edit`}>
              {categories.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Título *
              <input
                type="text"
                value={draft.title}
                onChange={(event) => updateDraft({ title: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Categoria
              <input
                list={categories && categories.length > 0 ? `${datalistId}-edit` : undefined}
                type="text"
                value={draft.category}
                onChange={(event) => updateDraft({ category: event.target.value })}
                className={inputClass}
              />
            </label>
            {showCounterparty && (
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Contraparte
                <input
                  type="text"
                  value={draft.counterpartyName}
                  onChange={(event) => updateDraft({ counterpartyName: event.target.value })}
                  className={inputClass}
                />
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Referência
              <input
                type="text"
                value={draft.reference}
                onChange={(event) => updateDraft({ reference: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data
              <input
                type="date"
                value={draft.documentDate}
                onChange={(event) => updateDraft({ documentDate: event.target.value })}
                className={inputClass}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Notas
              <textarea
                rows={3}
                value={draft.notes}
                onChange={(event) => updateDraft({ notes: event.target.value })}
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );

  // Inline variant and the obra-tab (hideTitle) usage render loose — no outer card —
  // so the documents list sits flush like the sibling tabs. Standalone
  // client/supplier sections keep the framing card.
  if (isInline || hideTitle) {
    return body;
  }

  return (
    <div className="bg-card border border-border p-5" style={{ borderRadius: "var(--radius-card)" }}>
      {body}
    </div>
  );
}
